/**
 * Forecast Overrides — user-edited values stored in localStorage.
 *
 * Source data (forecast2026.js, monthlyBurnByYear) is treated as the BASELINE.
 * When the user edits a cell, an override is recorded here. Reads merge:
 *   override (if present) → source (if not).
 *
 * Key formats:
 *   - Monthly child dept:  `m:${childDept}:${year}-${monthIdx}`   value in $
 *   - Annual parent dept:  `y:${parentId}:${year}`                value in $
 *
 * Subscribe via subscribe() to react to changes (chart/table re-renders).
 */

import { forecastByYear, FY_LIST } from './forecast';
import { deptHierarchy } from './deptHierarchy';

const STORAGE_KEY = 'finkloz.forecast.overrides';

let cache = (() => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
})();

const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {/* quota — ignore */}
  notify();
}

// ─── Monthly child dept — full 2026-2031 detail from forecast PDF ───────────
export function getMonthly(childDept, year, monthIdx) {
  const k = `m:${childDept}:${year}-${monthIdx}`;
  if (k in cache) return cache[k];
  return forecastByYear[year]?.[childDept]?.[monthIdx] ?? 0;
}

export function setMonthly(childDept, year, monthIdx, value) {
  const k = `m:${childDept}:${year}-${monthIdx}`;
  if (value == null || isNaN(value)) delete cache[k];
  else cache[k] = Math.round(Number(value));
  persist();
}

export function clearMonthly(childDept, year, monthIdx) {
  delete cache[`m:${childDept}:${year}-${monthIdx}`];
  persist();
}

// ─── Annual parent dept (used for 2027+ where we don't have monthly detail) ──
export function getAnnualParent(parentId, year) {
  const k = `y:${parentId}:${year}`;
  if (k in cache) return cache[k];
  return null; // caller falls back to baseline computation
}

export function setAnnualParent(parentId, year, value) {
  const k = `y:${parentId}:${year}`;
  if (value == null || isNaN(value)) delete cache[k];
  else cache[k] = Math.round(Number(value));
  persist();
}

// ─── Reset everything ─────────────────────────────────────────────────────────
export function resetAll() {
  cache = {};
  persist();
}

export function hasOverrides() {
  return Object.keys(cache).length > 0;
}

export function overrideCount() {
  return Object.keys(cache).length;
}

// ─── Computed views ──────────────────────────────────────────────────────────

/**
 * Annual amount for a child dept × year — sums monthly (with overrides applied).
 * Works for all years 2026-2031 since we have full source data.
 */
export function annualForChildDept(childDept, year) {
  let total = 0;
  for (let m = 0; m < 12; m++) total += getMonthly(childDept, year, m) || 0;
  return total;
}

/**
 * Annual amount for a parent dept × year.
 * Override (if explicitly set on the parent) takes precedence; otherwise rolls
 * up children using getMonthly + overrides.
 */
export function annualForParentDept(parentId, year) {
  const override = getAnnualParent(parentId, year);
  if (override != null) return override;
  const children = deptHierarchy[parentId] || [];
  return children.reduce((s, c) => s + annualForChildDept(c, year), 0);
}
